import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzClearableInputWebComponent } from '../../../../ui/gzInput';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { IntegrationType } from '../../../types/IntegrationType';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { LicensingConstants } from '../../../common/licensingTab/LicensingConstants';
import { Subscription } from 'rxjs';
import { CompanyData } from '../../../types/company-data';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { htmlEncode } from '../../../../utils/escape.utils';
import { AffectedCompanyImportingInaccessibleMdrContacts } from '../../../../types/common/mdrTypes';
import { buildCsvForInaccessibleMdrContacts } from '../../../../utils/mdr.utils';
import { translateFn } from '../../../../utils/translator.helper';

export class ChangePartnerMenuView extends BaseWebComponentView {
  private partnerIdFieldSubscription: Subscription | null = null;

  public dom: {
    $wrapper: HTMLElement;
    $innerContainer: HTMLElement;

    $companyName: GzTitleWebComponent;
    $menuTitle: GzTitleWebComponent;
    $infoText: HTMLElement;
    $formTitle: GzTitleWebComponent;
    $companyIdLabel: HTMLElement;
    $companyIdFormField: GzFormFieldWebComponent<GzClearableInputWebComponent, string>;

    $backendErrorContainer: HTMLElement;
    $backendErrorText: HTMLElement;

    $ec2InfoBox: GzInfoBoxWebComponent;

    $changePartnerButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#change-partner-wrapper'),
      $innerContainer: this.shadow.querySelector('#change-partner-inner-container'),

      $companyName: this.shadow.querySelector('#change-partner-company-name'),
      $menuTitle: this.shadow.querySelector('#change-partner-title'),
      $infoText: this.shadow.querySelector('#change-partner-info-text'),
      $formTitle: this.shadow.querySelector('#change-partner-form-title'),
      $companyIdLabel: this.shadow.querySelector('#change-partner-company-id-label'),
      $companyIdFormField: this.shadow.querySelector('#change-partner-company-id'),

      $backendErrorContainer: this.shadow.querySelector('#change-partner-backend-error-container'),
      $backendErrorText: this.shadow.querySelector('#change-partner-backend-error-text'),
      $ec2InfoBox: this.shadow.querySelector('#change-partner-ec2-notice-container'),

      $changePartnerButton: this.shadow.querySelector('#change-partner-submit-button'),
      $cancelButton: this.shadow.querySelector('#change-partner-cancel-button'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
    this.buildEc2InfoBox();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    // change partner side menu
    this.dom.$menuTitle.innerText = this.dependencies.translateFn('Change partner');
    this.dom.$infoText.innerText = this.dependencies.translateFn(
      'To switch to your new Bitdefender partner, register its company hash in this form. In case you do not have the hash already, contact your new partner to provide it to you.'
    );
    this.dom.$formTitle.innerText = this.dependencies.translateFn('Partner Company');
    this.dom.$companyIdLabel.innerText = this.dependencies.translateFn('Company hash');

    this.dom.$changePartnerButton.innerText = this.dependencies.translateFn('Change Partner');
    this.dom.$cancelButton.innerText = this.dependencies.translateFn('Cancel');
  }

  private buildEc2InfoBox(): void {
    this.dom.$ec2InfoBox.innerHTML = `
        <span><strong>${htmlEncode(this.dependencies.translateFn('Amazon EC2 integration change'))}</strong></span><br>
        <p>${htmlEncode(
          this.dependencies.translateFn(
            "If you have an AWS Marketplace subscription and move under a Partner, your subscription is changed to a Partner subscription. AWS subscriptions cannot be moved under a Partner that doesn't offer Security for AWS."
          )
        )}</p>
        <p>${htmlEncode(
          this.dependencies.translateFn(
            'You can move to a Partner that does not have an active Security for AWS subscription only after you delete your Amazon EC2 integrations.'
          )
        )}</p>
    `;
  }

  buildSuccessView() {
    const hasEc2Enabled =
      this.dependencies.IntegrationsLib.self.hasCloudIntegrationVisible(IntegrationType.Aws) &&
      this.dependencies.IntegrationsLib.self.hasCloudIntegrationConfigured(IntegrationType.Aws);

    this.dom.$ec2InfoBox.classList.toggle('hidden', !hasEc2Enabled);

    this.dom.$companyIdFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(
        /^[A-Fa-f0-9]{32}$/,
        this.dependencies.translateFn(
          'Must contain {partnerIdLength} characters only within these ranges: 0-9 and a-f',
          {
            partnerIdLength: LicensingConstants.PartnerIdLength,
          }
        )
      ),
    ];

    this.partnerIdFieldSubscription = this.dom.$companyIdFormField.valid$.subscribe({
      next: (valid: boolean) => {
        this.dom.$changePartnerButton.disabled = !valid;
      },
    });
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public setCompany(company: CompanyData) {
    this.dom.$companyName.innerText = company.name;
  }

  protected onDisconnect() {
    this.partnerIdFieldSubscription?.unsubscribe();
  }

  public setMdrCustomerBackendErrorText(): void {
    this.dom.$backendErrorText.innerHTML = translateFn(
      'Cannot change the Partner right now. Go to {OPEN_TAG}My company > Company details{CLOSE_TAG} and enter new MDR contact details.',
      { OPEN_TAG: '<strong>', CLOSE_TAG: '</strong>' }
    );
  }

  public setMdrPartnerBackendErrorText(affectedCompanies: AffectedCompanyImportingInaccessibleMdrContacts[]): void {
    const csvContent = buildCsvForInaccessibleMdrContacts(affectedCompanies);

    this.dom.$backendErrorText.innerHTML = translateFn(
      'Cannot change the Partner right now. One or more of your managed companies are using MDR contact details from partners that will become inaccessible. Update the MDR contacts for the affected companies using {OPEN_TAG}this downloadable CSV.{CLOSE_TAG} Once updated, try again.',
      {
        OPEN_TAG: `<gz-link href="${csvContent}" download="companies-mdr.csv">`,
        CLOSE_TAG: '</gz-link>',
      }
    );
  }
}
